import { useCallback, useEffect, useMemo, useState } from "react";
import { useAccount } from "wagmi";
import { copyToClipboard, numberToCurrency } from "utils";
import { AiOutlineInfoCircle } from "react-icons/ai";

import {
  Box,
  Flex,
  Text,
  Divider,
  Button,
  useToast,
  Tooltip,
  Skeleton,
} from "@chakra-ui/react";
import { getDashboardata } from "services";

import { INITIAL_REWARS_DATA } from "@constants/common";

import PointsDistributionTable from "@components/PointsDistributionTable";
import BaseImage from "@components/BaseImage";
// import { KELP_GRAND_MILES_CONVERSION } from "@constants/analytics";
import DashboardBanner from "./DashboardBanner";
import useSanction from "@hooks/useSanction";
import SanctionBanner from "@components/SanctionBanner";
// import { useQuery } from "@tanstack/react-query";

// import useKelpMiles from "@hooks/useKelpMiles";

const DashboardComponent = () => {
  const [dashboardData, setDasboardData] = useState<
    typeof INITIAL_REWARS_DATA | null
  >(INITIAL_REWARS_DATA);
  const [isRewardsLabelOpen, setIsRewardsLabelOpen] = useState(false);
  // const [isSeason1LabelOpen, setSeason1LabelOpen] = useState(false);
  const [isLoadingDashboardData, setIsLoadingDashboardData] = useState(false);

  const { address } = useAccount();
  const { isSanctioned } = useSanction();
  const toast = useToast();

  // const { data: kernelStatsSeason1, isLoading: isLoadingSeason1Data } =
  //   useQuery({
  //     queryKey: ["kernel-season1-eth", address],
  //     queryFn: () => {
  //       return checkTokenEligibilityETH(address!);
  //     },
  //     enabled: address !== undefined,
  //   });

  // const kernelSeason1ResultETH = kernelStatsSeason1?.data;

  // const { data: kernelStatsSeason1BNB, isLoading: isLoadingSeason1DataBNB } =
  //   useQuery({
  //     queryKey: ["kernel-season1-bnb", address],
  //     queryFn: () => {
  //       return checkTokenEligibilityBNB(address!);
  //     },
  //     enabled: address !== undefined,
  //   });

  // const kernelSeason1ResultBNB = kernelStatsSeason1BNB?.data;

  // const isLoadingSeason1 = useMemo(() => {
  //   return isLoadingSeason1DataBNB || isLoadingSeason1Data;
  // }, [isLoadingSeason1Data, isLoadingSeason1DataBNB]);

  // const claimableSeason1Amount = useMemo(() => {
  //   const ethClaimable = kernelSeason1ResultETH?.data?.kernelPoints || 0;
  //   const bnbClaimable = kernelSeason1ResultBNB?.data?.kernelPoints || 0;

  //   return Number(ethClaimable) + Number(bnbClaimable);
  // }, [kernelSeason1ResultBNB, kernelSeason1ResultETH]);

  const getPoints = useCallback(async () => {
    if (address) {
      const result = await getDashboardata(address);
      setDasboardData(result?.data?.value || null);
      setIsLoadingDashboardData(false);
    }
  }, [address]);

  const getKelpValue = useMemo(() => {
    return (
      <Text
        color="textPrimary"
        fontSize={{ base: "14px", md: "20px" }}
        lineHeight="120%"
      >
        {dashboardData?.kelpMiles
          ? numberToCurrency((+dashboardData.kelpMiles / 1000).toFixed(1), 1)
          : "-"}
      </Text>
    );
  }, [dashboardData]);

  const renderTooltipContent = () => {
    // trackEvent(REFERRAL_INFO);
    return (
      <Box gap="2px" pointerEvents="all">
        <Text display="inline">
          Your friends earn 10% extra Kernel Points through your link. You earn
          upto 20% of Kernel Points accrued by your referrals. Check details
        </Text>

        <Text
          ml="2px"
          display="inline"
          color="textPrimary"
          as="a"
          href="https://kerneldao.gitbook.io/kernel/getting-started/kelp/explore-rseth/referrals"
          target="_blank"
          rel="noreferrer"
          cursor="pointer"
          fontWeight="700"
          textDecor="underline"
        >
          here
        </Text>
      </Box>
    );
  };

  useEffect(() => {
    if (address) {
      setIsLoadingDashboardData(true);
      getPoints();
    } else {
      setDasboardData(null);
    }
  }, [address, getPoints]);

  const getWalletConnectWarning = useCallback((text: string) => {
    return (
      <Flex
        color="textPrimary"
        bg="#ECBB85"
        borderRadius="12px"
        p="8px"
        gap="8px"
        alignItems="center"
      >
        <Box
          height={{ base: "16px", md: "24px" }}
          width={{ base: "16px", md: "24px" }}
          pos="relative"
        >
          <BaseImage src="/assets/info.svg" alt="info" fill />
        </Box>

        <Text fontSize={{ base: "12px", md: "14px" }}>{text}</Text>
      </Flex>
    );
  }, []);

  const kelpMilesForReferrer = (() => {
    const value = Number(dashboardData?.kelpMilesForReferrer);

    if (typeof value === "number" && !isNaN(value)) {
      return numberToCurrency((value / 1000).toFixed(1), 1);
    }

    return "-";
  })();

  return (
    <>
      <Flex justifyContent="center" p={{ base: "0 16px", md: 0 }} mb="10px">
        <Flex
          flexDir="column"
          w={{ base: "100%", md: "748px" }}
          mt={{ base: "16px", xl: 0 }}
        >
          {isSanctioned && <SanctionBanner />}
        </Flex>
      </Flex>
      <Flex
        justifyContent="center"
        p={{ base: "0 16px", md: 0 }}
        mb="60px"
        pointerEvents={isSanctioned ? "none" : "auto"}
        opacity={isSanctioned ? 0.5 : 1}
      >
        <Flex
          borderRadius="24px"
          flexDir="column"
          w={{ base: "100%", md: "748px" }}
          mt={{ base: "16px", xl: 0 }}
          gap="32px"
        >
          <Text
            fontWeight="600"
            fontSize="32px"
            lineHeight="38px"
            color="textPrimary"
          >
            Dashboard
          </Text>

          <Flex
            gap="16px"
            flexDir={{ base: "column", md: "row" }}
            fontWeight="500"
          >
            <Flex
              p={{ base: "20px 16px", md: "24px" }}
              borderRadius={{ base: "16px", md: "24px" }}
              bg="backgroundCard"
              flexDir="column"
              gap={{ base: "16px", md: "40px" }}
              w={{ base: "100%", md: "374px" }}
            >
              <Box>
                <Text
                  color="textPrimary"
                  fontWeight="500"
                  fontSize={{ base: "16px", md: "20px" }}
                  lineHeight="120%"
                >
                  Your rewards
                </Text>

                <Divider
                  color="textPrimary"
                  mt={{ base: "16px", md: "24px" }}
                  h="1px"
                  opacity="0.8"
                  borderColor="#B6DFDF"
                />
              </Box>

              {/* <Flex
                alignItems="flex-start"
                // justify="space-between"
                gap="16px"
              >
                <BaseImage
                  src="/assets/kernel.svg"
                  width={24}
                  height={24}
                  alt="kelp"
                />
                <Flex flexDir="column">
                  <Text
                    color="textPrimary"
                    fontSize={{ base: "14px", md: "20px" }}
                    lineHeight="120%"
                  >
                    {address ? (
                      isLoadingSeason1 ? (
                        <Skeleton height="20px" w="60px" />
                      ) : (
                        numberToCurrency(claimableSeason1Amount.toString(), 1)
                      )
                    ) : (
                      "-"
                    )}
                  </Text>
                  <Flex alignItems="center" gap="4px">
                    <Text
                      color="textSecondary"
                      fontSize={{ base: "12px", md: "14px" }}
                      lineHeight="120%"
                      mt="2px"
                    >
                      Season 1 Kernel points
                    </Text>

                    <Tooltip
                      isOpen={isSeason1LabelOpen}
                      closeDelay={2000}
                      onClose={() => setSeason1LabelOpen(false)}
                      label={
                        <Text display="inline">
                          Your Kernel Points accumulated till 31st Dec 2024 for
                          activity across Kelp, Gain, and Kernel products
                        </Text>
                      }
                      color="textSecondary"
                      fontSize="12px"
                      bg="backgroundCard"
                      placement="top"
                      p="8px"
                      borderRadius="8px"
                      pointerEvents="all"
                    >
                      <span>
                        <AiOutlineInfoCircle
                          onMouseEnter={() => setSeason1LabelOpen(true)}
                          onClick={() => setSeason1LabelOpen(true)}
                        />
                      </span>
                    </Tooltip>
                  </Flex>
                </Flex>
              </Flex> */}

              <Flex
                alignItems="flex-start"
                // justify="space-between"
                gap="16px"
              >
                <BaseImage
                  src="/assets/kernel.svg"
                  width={24}
                  height={24}
                  alt="kelp"
                />
                <Flex flexDir="column">
                  <Text
                    color="textPrimary"
                    fontSize={{ base: "14px", md: "20px" }}
                    lineHeight="120%"
                  >
                    {address ? (
                      isLoadingDashboardData ? (
                        <Skeleton height="20px" w="60px" />
                      ) : (
                        getKelpValue
                      )
                    ) : (
                      "-"
                    )}
                  </Text>
                  <Text
                    color="textSecondary"
                    fontSize={{ base: "12px", md: "14px" }}
                    lineHeight="120%"
                    mt="2px"
                  >
                    Total Kernel points
                  </Text>
                </Flex>
              </Flex>

              {!address &&
                getWalletConnectWarning("Connect wallet to view rewards")}

              <Box
                bg={"modalOverlay"}
                borderRadius={"8px"}
                p={"12px"}
                // mt={{ base: "16px", md: "24px" }}
              >
                {/* <Text
                  color={"white"}
                  fontWeight={400}
                  lineHeight={"18px"}
                  fontSize={"12px"}
                  >
                  Your Kelp Miles have been updated.🌟
                  </Text> */}
                <Text
                  color={"white"}
                  fontWeight={400}
                  lineHeight={"14px"}
                  fontSize={"12px"}
                  opacity={0.8}
                >
                  Kelp Grand Miles are now Kernel Points.
                  <br /> 1 Kelp Grand Mile = 1 Kernel Point. Your overall points
                  balance remains the same.
                </Text>
              </Box>
            </Flex>
            <Flex
              p={{ base: "20px 16px", md: "24px" }}
              borderRadius={{ base: "16px", md: "24px" }}
              bg="backgroundCard"
              flexDir="column"
              gap={{ base: "16px", md: "40px" }}
              w={{ base: "100%", md: "374px" }}
            >
              <Box>
                <Flex gap="8px" align="center">
                  <Text
                    color="textPrimary"
                    fontWeight="500"
                    fontSize={{ base: "16px", md: "20px" }}
                    lineHeight="120%"
                  >
                    Referral
                  </Text>

                  <Tooltip
                    isOpen={isRewardsLabelOpen}
                    closeDelay={2000}
                    onClose={() => setIsRewardsLabelOpen(false)}
                    label={renderTooltipContent()}
                    color="textSecondary"
                    fontSize="12px"
                    bg="backgroundCard"
                    placement="top"
                    p="8px"
                    borderRadius="8px"
                    pointerEvents="all"
                  >
                    <span>
                      <AiOutlineInfoCircle
                        onMouseEnter={() => setIsRewardsLabelOpen(true)}
                        onClick={() => setIsRewardsLabelOpen(true)}
                      />
                    </span>
                  </Tooltip>
                </Flex>
                <Divider
                  color="textPrimary"
                  mt={{ base: "16px", md: "24px" }}
                  h="1px"
                  opacity="0.8"
                  borderColor="#B6DFDF"
                />
              </Box>
              <Flex
                alignItems="flex-start"
                // justify="space-between"
                gap="16px"
              >
                <BaseImage
                  src="/assets/kernel.svg"
                  width={24}
                  height={24}
                  alt="kelp"
                />
                <Flex flexDir="column">
                  {isLoadingDashboardData ? (
                    <Skeleton height="20px" w="60px" />
                  ) : (
                    <Text
                      color="textPrimary"
                      fontSize={{ base: "14px", md: "20px" }}
                      lineHeight="120%"
                    >
                      {kelpMilesForReferrer}
                    </Text>
                  )}

                  <Text
                    color="textSecondary"
                    fontSize={{ base: "12px", md: "14px" }}
                    lineHeight="120%"
                    mt="2px"
                  >
                    Kernel Points
                  </Text>
                </Flex>
              </Flex>
              {address ? (
                <Button
                  w="100%"
                  h="44px"
                  bg="backgroundCard"
                  borderRadius="12px"
                  border="1px solid"
                  borderColor="textSecondary"
                  _hover={{
                    bg: "backgroundCard",
                  }}
                  onClick={() => {
                    copyToClipboard(
                      `https://kelpdao.xyz/restake/?utm_source=${address}`
                    );
                    toast({
                      description: "Referral link copied",
                      status: "success",
                      duration: 2000,
                      position: "top",
                      isClosable: false,
                    });
                  }}
                >
                  <Text
                    fontSize="14px"
                    lineHeight="24px"
                    letterSpacing="0.3px"
                    fontWeight="500"
                    color="textPrimary"
                  >
                    Copy referral link
                  </Text>
                </Button>
              ) : (
                <Text fontSize="14px">
                  Earn upto 20% of Kernel Points accrued by your referrals
                </Text>
              )}

              {!address &&
                getWalletConnectWarning(
                  "Connect wallet to refer your community"
                )}
            </Flex>
          </Flex>

          <DashboardBanner />

          <PointsDistributionTable
            dashboardData={dashboardData}
            isLoading={isLoadingDashboardData}
          />
        </Flex>
      </Flex>
    </>
  );
};

export default DashboardComponent;
